<template>
  <div
    id="reviews"
    :class="[$b(), 'ignore-theme']"
  >
    <div
      v-if="!failedToLoad"
      :class="$b('reviews-container')"
    >
      <div :class="$b('reviews')">
        <ReviewCard
          v-for="review in loadedReviews"
          :key="review.id"
          :review="review"
          :link-product-image="!useGenericReviews"
          :product-name="productName(review)"
          in-dialog
          :is-on-product-page="isOnProductPage"
          @product-click="handleClick"
        />
      </div>
      <div ref="more" />
      <client-only>
        <InfiniteScroll
          :class="$b('loading-spinner')"
          :enough="!morePagesAvailable"
        >
          <LoadingOverlay
            :loading="true"
            :full-screen="false"
          />
        </InfiniteScroll>
      </client-only>
    </div>
  </div>
</template>
<script setup>
import { reviewsListingQuery } from '~/gql/reviews.gql'

defineOptions({
  name: 'InfiniteReviews'
})

const props = defineProps({
  useGenericReviews: { type: Boolean, default: true },
  productIds: { type: [Array, String], default: () => [] },
  skusForReviews: { type: [Array, String], default: () => [] },
  showLocalisedReviews: { type: Boolean, default: false },
  isOnProductPage: { type: Boolean, default: true }
})

const emit = defineEmits([
  'update:showLocalisedReviews',
  'update:showReviewsToggle',
  'update:summaryCount',
  'reviewProductClicked'
])

const page = ref(0)
const failedToLoad = ref(false)
const observer = ref(null)
const loadedReviews = ref([])
const pageSize = ref(20)
const pagesLoaded = ref([])
const summary = ref({})
const localCountThreshold = ref(10)
const productStore = useProductStore()
const { storeCountry } = useStorefront()
const { $sentry } = useNuxtApp()
const more = ref(null)

const summaryKey = computed(() => {
  return props.productIds.join(',')
})

const useLocalisedReviews = computed(() => {
  return props.useGenericReviews
    ? localCountThreshold.value <= summary.value?.local_count
    : localCountThreshold.value <=
        summary.value[summaryKey.value]?.local_count
})

const morePagesAvailable = computed(() => {
  const loadedReviewsCount = Object.keys(loadedReviews.value).length
  let reviewCount

  if (props.useLocalisedReviews) {
    reviewCount = props.useGenericReviews
      ? summary.value?.local_count
      : summary.value[summaryKey.value]?.local_count
  } else {
    reviewCount = props.useGenericReviews
      ? summary.value?.count
      : summary.value[summaryKey.value]?.count
  }

  return loadedReviewsCount < reviewCount
})

async function loadPage(pageNumber, local = false) {
  if (page.value === pageNumber || pageNumber <= 0) return

  if (!pagesLoaded.value.includes(pageNumber)) {
    await getReviews(
      pageNumber,
      props.useGenericReviews,
      local,
      summaryKey.value
    )
  }

  page.value = pageNumber
  pagesLoaded.value.push(pageNumber)
}

function handleClick(product) {
  emit('reviewProductClicked', product)
}

function productName(review) {
  if (!review.items[0]) return null

  return productStore.productName(review.items[0].split('-')[0]) || null
}

function intersectionObserver(local = false) {
  const target = more.value
  observer.value = new IntersectionObserver(
    async ([entry]) => {
      if (entry.isIntersecting) {
        await loadPage(page.value + 1, local)
      }
    },
    {
      root: document.getElementById('reviews')[0],
      rootMargin: '800px',
      threshold: 0,
    }
  )
  // start observing
  observer.value.observe(target)
}

function setReviews(data, offset, generic = false, key = null) {
  const reviews = loadedReviews.value || []

  const existingIds = Object.values(reviews).map((review) => review.id)

  const filteredReviews = data.reviews.filter(
    (review) => !existingIds.includes(review.id)
  )

  let processedReviews = []

  const unwantedRegexPatten = /case-|gift|donations|donation|microfib|gogglelensprotector|lenscleaningkit|poster-|lrk-|parts-/

  if (generic) {
    processedReviews = filteredReviews.map((review, idx) => {
      review.items = review.skus.filter((item) => {
        if (!item || item.match(unwantedRegexPatten)) return false
        return true
      })

      return [offset + idx, review]
    })
  } else if (key) {
    const validSkus = Array.isArray(props.skusForReviews)
      ? props.skusForReviews
      : [props.skusForReviews]
    
    processedReviews = filteredReviews.map((review, idx) => {
      review.items = review.skus.filter((item) => {
        if (!item || item.match(unwantedRegexPatten)) return false
        if (validSkus.some(sku => item.startsWith(sku))) return true
        return false
      })

      return [offset + idx, review]
    })
  }

  loadedReviews.value = {
    ...reviews,
    ...Object.fromEntries(processedReviews),
  }
}

async function getReviews(page, generic = false, local = false, key) {
  const variables = {
    skip: (page - 1) * pageSize.value,
    limit: pageSize.value,
    filter: {},
    globalFilter: {}
  }

  if (props.productIds?.length > 0 && !generic) {
    variables.filter.products = { anyIn: props.productIds }
    variables.globalFilter.products = { anyIn: props.productIds }
  }

  if (local) {
    variables.filter.country = { eq: storeCountry.value }
  }
  
  try {
    const { data } = await useDatoQuery({
      query: reviewsListingQuery,
      variables
    })

    setReviews(data.value, variables.skip, generic, key)
    storeSummary(
      data.value.local.count,
      data.value.global.count,
      generic,
      key
    )
  } catch (error) {
    $sentry.captureException(error)
    console.error(error)
  }
}

function storeSummary(local, global, generic = false, key = null) {
  if (generic) {
    summary.value = { local, global }
  } else if (key) {
    summary.value[key] = { local, global }
  }

  emitReviewSummary(generic, key)
}

function emitReviewSummary(generic = false, key = null) {
  const relevantSummary = generic
    ? summary.value
    : summary.value[key]
  return emit('update:summaryCount', {
    local: relevantSummary.local,
    global: relevantSummary.global
  })
}

await loadPage(1, useLocalisedReviews.value)

watch(
  () => props.showLocalisedReviews,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (observer.value) {
        observer.value.disconnect()
      }

      page.value = 0
      loadedReviews.value = []
      pagesLoaded.value = []

      getReviews(
        1,
        props.useGenericReviews,
        props.showLocalisedReviews,
        summaryKey.value
      )
      intersectionObserver(props.showLocalisedReviews)
      emit('update:showReviewsToggle', useLocalisedReviews.value)
    }
  }
)

onMounted(() => {
  intersectionObserver(useLocalisedReviews.value)

  emitReviewSummary(
    props.useGenericReviews,
    summaryKey.value
  )

  emit('update:showLocalisedReviews', useLocalisedReviews.value)
  emit('update:showReviewsToggle', useLocalisedReviews.value)
})

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
</script>

<style lang="postcss">
.InfiniteReviews {
  @apply pb-0 lg:pb-xl
    mdMax:h-full;
}

.InfiniteReviews__reviews-container {
  @apply relative
    mdMax:h-full max-w-3xl;
}

.InfiniteReviews__reviews {
  @apply flex flex-col gap-y-12 md:gap-y-10;
}

.InfiniteReviews__reviews-carousel {
  @apply md:hidden
    mdMax:h-full;
}

.InfiniteReviews__header {
  @apply hidden md:flex flex-col md:flex-row items-start justify-between md:items-start
    pb-sm lg:pb-md;
}

.InfiniteReviews__header-heading {
  @apply mdMax:text-2xl;
}

.InfiniteReviews__stars-container {
  @apply flex flex-row flex-wrap items-center justify-start
    mt-sm md:mt-md;
}

.InfiniteReviews__products {
  @apply -mx-sm px-sm
    mdMax:h-full;
}

.InfiniteReviews__loading-spinner {
  @apply mdMax:hidden;
}
</style>
